<template>
	<article class="immunity-estimation-result">
		<!-- ローディング -->
		<transition name="fade">
			<aside v-if="isLoading" class="immunity-estimation-result__loading">
				<webview-redraw-progress />
			</aside>
		</transition>
		<!-- /ローディング -->

		<section class="immunity-estimation-result__report">
			<!-- 免疫力判定 -->
			<section class="immunity-estimation-result__report--judgement">
				<h2 class="immunity-estimation-result__report--judgement-title">免疫力判定</h2>
				<p class="immunity-estimation-result__report--judgement-period">
					{{ estimationStartDate }} 〜 {{ estimationEndDate }}のスコア
				</p>
				<div class="immunity-estimation-result__report--judgement-canvas">
					<doughnut-chart
						:probability="computedDoughnutChartSetData.probability"
						:restProbability="computedDoughnutChartSetData.restProbability"
						:segmentColor="['#FF6384', '#FFCACA']"
						:chartIndex="computedDoughnutChartSetData.key"
						:key="`immunity-estimation-doughnut-chart-${computedDoughnutChartSetData.key}`"
					/>
				</div>
				<p class="immunity-estimation-result__report--judgement-complastweek">先週比： {{ prevProbability }}点</p>
				<div v-if="!isAndroid" class="immunity-estimation-result__report--judgement-column">
					<a href="https://zutool.jp/selfcheck_list/" target="_blank" rel="noopener noreferrer">
						<img src="@/assets/img/immunity-estimation/selfcheck_list_img.png" alt="セルフチェックリスト">
					</a>
				</div>
			</section>
			<!-- /免疫力判定 -->

			<!-- 免疫力スコア推移 -->
			<section class="immunity-estimation-result__report--graph">
				<h2 class="immunity-estimation-result__report--graph-title">免疫力スコア推移</h2>
				<div class="immunity-estimation-result__report--graph-date">
					<svg @click="reloadDate('pre')" class="pre" width="20" height="20" viewBox="0 0 20 20" fill="none">
						<path d="M13 15.9999L7 10.0161L13 3.99994" stroke-width="2" />
					</svg>
					<span
						@click="reloadDate('now')"
						:class="[
							'date',
							{ 'is-date': selectType === 'day' },
							{ 'is-disabled': isDisabledNext }
						]"
					>
						{{ computedDate }}
					</span>
					<svg
						@click="reloadDate('post')"
						:class="[
							'post',
							{ 'is-disabled': isDisabledNext }
						]"
						width="16"
						height="14"
						viewBox="0 0 16 14"
						fill="none"
					>
						<path d="M1 1.00006L7 6.98395L1 13.0001" stroke-width="2" />
					</svg>
				</div>
				<div class="immunity-estimation-result__report--graph-canvas">
					<line-chart
						:labels="computedLineChartSetData.value_labels"
						:datapoints="computedLineChartSetData.value_data"
						:chartIndex="computedLineChartSetData.value_key"
						:displayType="selectType"
						:key="`immunity-estimation-line-chart-${computedLineChartSetData.value_key}`"
						:yAxisMaxValue="100"
					/>
				</div>
				<div class="immunity-estimation-result__report--graph-tab">
					<ul class="list">
						<li :class="selectType === '1month' ? 'list-active': ''" @click="tabClick">1ヶ月</li>
						<li :class="selectType === '6month' ? 'list-active': ''" @click="tabClick">6ヶ月</li>
						<li :class="selectType === 'year' ? 'list-active': ''" @click="tabClick">1年</li>
					</ul>
				</div>
			</section>
			<!-- /免疫力スコア推移 -->

			<!-- 先週の振り返り -->
			<section class="immunity-estimation-result__report--lookback">
				<h2 class="immunity-estimation-result__report--lookback-title">先週の振り返り</h2>
				<h3 class="immunity-estimation-result__report--lookback-subtitle">気圧アラート出現傾向</h3>
				<div class="immunity-estimation-result__report--lookback-weekalerts">
					<p class="title">先週</p>
					<ul class="lists">
						<li v-for="(item, index) in computedPressureAlerts" :key="`lastweekalerts-${index}`" class="lastweek">
							<p class="date">{{ item.date }}</p>
							<img :src="levelImgs[`level${item.alert === 0 ? 1 : item.alert}`]" :alt="`アラート${item.alert}`">
						</li>
					</ul>
				</div>
				<div class="immunity-estimation-result__report--lookback-weekalerts">
					<p class="title">先々週</p>
					<ul class="lists">
						<li v-for="(item, index) in computedPrevPressureAlerts" :key="`lastweekalerts-${index}`" class="twoweek">
							<p class="date">{{ item.date }}</p>
							<p class="alert" :class="item.alert === 0 ? 'bg_level_1' : `bg_level_${item.alert}`">{{ item.alert === 0 ? '通常' : item.alert === 1 ? '通常'
								: item.alert === 2 ? 'やや注意' : item.alert === 3 ? '注意' : item.alert === 4 ? '警戒' : item.alert === 5 ? '超警戒' : '上昇注意' }}</p>
						</li>
					</ul>
					<p class="comment">{{ prev_pressure_comment }}</p>
				</div>
				<div class="immunity-estimation-result__report--lookback-physicalcondition">
					<div class="healthdata">
						<div>
							<p class="title step">平均歩数</p>
							<div class="date">
								<p class="average" :class="computedHealthSetData.prev_average_step < 0 ? 'under' : 'above'"><span>{{ computedHealthSetData.average_step }}</span>歩</p>
								<p class="ratio"><span>前回比：{{ computedHealthSetData.prev_average_step }}</span>歩</p>
							</div>
						</div>
						<div>
							<p class="title sleep">平均睡眠時間</p>
							<div class="date">
								<p class="average" v-if="computedHealthSetData.average_sleep >= 60" :class="computedHealthSetData.prev_average_sleep < 0 ? 'under' : 'above'"><span>{{ computedHealthSetData.average_sleep_houre }}</span>時間<span>{{ computedHealthSetData.average_sleep_minutes }}</span>分</p>
								<p class="average" v-else :class="computedHealthSetData.prev_average_sleep < 0 ? 'under' : 'above'"><span>{{ 20 }}</span>分</p>
								<p class="ratio" v-if="computedHealthSetData.prev_average_sleep >= 60"><span>前回比：{{ computedHealthSetData.prev_average_sleep_houre }}</span>時間<span>{{ computedHealthSetData.prev_average_sleep_minutes }}</span>分</p>
								<p class="ratio" v-else><span>前回比：{{ computedHealthSetData.prev_average_sleep_minutes }}</span>分</p>
							</div>
						</div>
					</div>
					<p class="comment" v-if="computedHealthSetData.sleep_and_steps_comment" v-html="computedHealthSetData.sleep_and_steps_comment"></p>
					<div class="healthdata">
						<div>
							<p class="title sunshine">平均日照時間</p>
							<div class="date">
								<p class="average" v-if="computedHealthSetData.average_sunshine >= 60" :class="computedHealthSetData.prev_average_sunshine < 0 ? 'under' : 'above'"><span>{{ computedHealthSetData.average_sunshine_houre }}</span>時間<span>{{ computedHealthSetData.average_sunshine_minutes }}</span>分</p>
								<p class="average" v-else :class="computedHealthSetData.prev_average_sunshine < 0 ? 'under' : 'above'"><span>{{ computedHealthSetData.average_sunshine_minutes }}</span>分</p>
								<p class="ratio" v-if="computedHealthSetData.prev_average_sunshine >= 60"><span>前回比：{{ computedHealthSetData.prev_average_sunshine_houre }}</span>時間<span>{{ computedHealthSetData.prev_average_sunshine_minutes }}</span>分</p>
								<p class="ratio" v-else><span>前回比：{{ computedHealthSetData.prev_average_sunshine_minutes }}</span>分</p>
							</div>
						</div>
						<div>
							<p class="title temperature">平均前日気温差</p>
							<div class="date">
								<p class="average" :class="computedHealthSetData.prev_average_temperature_diff < 0 ? 'under' : 'above'"><span>{{ computedHealthSetData.average_temperature_diff }}</span>℃</p>
								<p class="ratio"><span>前回比：{{ computedHealthSetData.prev_average_temperature_diff }}</span>℃</p>
							</div>
						</div>
					</div>
					<div class="advice">
						<img src="@/assets/img/immunity-estimation/boost_your_immunity_ advice_title.png" alt="免疫アップのアドバイス">
						<p class="comment1">{{ computedHealthSetData.sleep_comment }}</p>
						<p class="comment2">{{ computedHealthSetData.pressure_comment }}</p>
					</div>
				</div>
			</section>
			<!-- /先週の振り返り -->

			<!-- 免疫と季節・天候のヒント -->
			<section class="immunity-estimation-result__report--hint">
				<h2 class="immunity-estimation-result__report--hint-title">免疫と季節・天候のヒント</h2>
				<img src="@/assets/img/immunity-estimation/maro.png" alt="免疫が弱まる時に気を付けたい「季節の指数と対策のヒント」をお知らせするぞい。" class="immunity-estimation-result__report--hint-speechbubble">
				<div class="immunity-estimation-result__report--hint-weatherindex">
					<div class="content">
						<p class="title">注目の健康天気指数</p>
						<div class="index">
							<h3>{{ computedHealthWeatherData.title }}</h3>
							<div>
								<span class="sprite_health_weather_icon" :class="`${computedHealthWeatherData.indexName}_icon`">
									<i :class="`sprite_health_weather_icon_lv ${computedHealthWeatherData.indexName}_icon_lv ${computedHealthWeatherData.indexName}_icon_lv${computedHealthWeatherData.level}`"></i>
								</span>
							</div>
						</div>
						<div class="comment">
							<p>{{ computedHealthWeatherData.comment }}</p>
						</div>
					</div>
					<a class="weatherlink" href="native://open_weather_forecast?tag=linkHealthWeather">
						健康天気指数をもっと見る <span>＞</span>
					</a>
				</div>
				<div v-if="!isAndroid" class="immunity-estimation-result__report--hint-column">
					<p class="title">
						<span class="first">そんなあなたに！</span>
						<span class="second">頭痛ーるより</span>
						<span class="third">オススメの読み物をご紹介</span>
					</p>
					<div class="article">
						<a href="javascript:void(0);" class="column1" @click="onColumn(computedColumnSetData.url[0])">
							<img :src="columnImgs[`column${computedColumnSetData.month}_1`]" alt="オススメコラム1">
						</a>
						<a href="javascript:void(0);" class="column2" @click="onColumn(computedColumnSetData.url[1])">
							<img :src="columnImgs[`column${computedColumnSetData.month}_2`]" alt="オススメコラム2">
						</a>
					</div>
				</div>
			</section>
			<!-- /免疫と季節・天候のヒント -->
		</section>
	</article>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_HEALTH_WEATHER } from '../../store/modules/weatherForecast/mutation-types'
import { SET_IMMUNITIES_SCORE_DATA, SET_USER_IMMUNITIES_DATA, SET_IMMUNITIES_LOOKINGBACK_DATA } from '../../store/modules/ai/mutation-types'

// Compornents
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'
import DoughnutChart from '@/components/Molecules/DoughnutChart'
import LineChart from '@/components/Molecules/LineChart'

// NOTE: 画像読み込み（vue3対応でViteでrequireが使用できないため）
import level1 from '@/assets/img/immunity-estimation/level_1.png'
import level2 from '@/assets/img/immunity-estimation/level_2.png'
import level3 from '@/assets/img/immunity-estimation/level_3.png'
import level4 from '@/assets/img/immunity-estimation/level_4.png'
import level5 from '@/assets/img/immunity-estimation/level_5.png'
import level6 from '@/assets/img/immunity-estimation/level_6.png'
import column11 from '@/assets/img/immunity-estimation/column_img_1-1.png'
import column12 from '@/assets/img/immunity-estimation/column_img_1-2.png'
import column21 from '@/assets/img/immunity-estimation/column_img_2-1.png'
import column22 from '@/assets/img/immunity-estimation/column_img_2-2.png'
import column31 from '@/assets/img/immunity-estimation/column_img_3-1.png'
import column32 from '@/assets/img/immunity-estimation/column_img_3-2.png'
import column41 from '@/assets/img/immunity-estimation/column_img_4-1.png'
import column42 from '@/assets/img/immunity-estimation/column_img_4-2.png'
import column51 from '@/assets/img/immunity-estimation/column_img_5-1.png'
import column52 from '@/assets/img/immunity-estimation/column_img_5-2.png'
import column61 from '@/assets/img/immunity-estimation/column_img_6-1.png'
import column62 from '@/assets/img/immunity-estimation/column_img_6-2.png'
import column71 from '@/assets/img/immunity-estimation/column_img_7-1.png'
import column72 from '@/assets/img/immunity-estimation/column_img_7-2.png'
import column81 from '@/assets/img/immunity-estimation/column_img_8-1.png'
import column82 from '@/assets/img/immunity-estimation/column_img_8-2.png'
import column91 from '@/assets/img/immunity-estimation/column_img_9-1.png'
import column92 from '@/assets/img/immunity-estimation/column_img_9-2.png'
import column101 from '@/assets/img/immunity-estimation/column_img_10-1.png'
import column102 from '@/assets/img/immunity-estimation/column_img_10-2.png'
import column111 from '@/assets/img/immunity-estimation/column_img_11-1.png'
import column112 from '@/assets/img/immunity-estimation/column_img_11-2.png'
import column121 from '@/assets/img/immunity-estimation/column_img_12-1.png'
import column122 from '@/assets/img/immunity-estimation/column_img_12-2.png'

// ライブラリ
import moment from 'moment'
moment.locale('ja')

export default {
	name: 'ImmunityEstimationResult',
	components: {
		WebviewRedrawProgress,
		DoughnutChart,
		LineChart
	},
	data () {
		return {
			levelImgs: { // NOTE: vue3対応でViteでrequireが使用できないため
				level1: level1,
				level2: level2,
				level3: level3,
				level4: level4,
				level5: level5,
				level6: level6
			},
			columnImgs: { // NOTE: vue3対応でViteでrequireが使用できないため
				column1_1: column11,
				column1_2: column12,
				column2_1: column21,
				column2_2: column22,
				column3_1: column31,
				column3_2: column32,
				column4_1: column41,
				column4_2: column42,
				column5_1: column51,
				column5_2: column52,
				column6_1: column61,
				column6_2: column62,
				column7_1: column71,
				column7_2: column72,
				column8_1: column81,
				column8_2: column82,
				column9_1: column91,
				column9_2: column92,
				column10_1: column101,
				column10_2: column102,
				column11_1: column111,
				column11_2: column112,
				column12_1: column121,
				column12_2: column122
			},
			isLoading: false,
			isDisabledNext: true,
			isDisabledNextDate: '',
			toDate: '', // 最新の推定日
			selectType: '1month',
			estimationStartDate: '', // 推定期間（開始）
			estimationEndDate: '', // 推定期間（終了）
			probability: 0, // 推定結果
			prevProbability: 0, // 先週比
			scoreTrendRecord: [], // 免疫力スコア推移,
			pressure_alerts: [], // 先週の気圧アラート
			prev_pressure_alerts: [], // 先々週の気圧アラート
			prev_pressure_comment: '' // 先週の気圧コメント
		}
	},
	created () {
	},
	mounted () {
		this.getData()
	},
	computed: {
		// map Vuex getters
		...mapGetters('common', ['userId', 'userToken', 'userData', 'isAndroid']),
		...mapGetters('ai', ['userImmunitiesData', 'immunityLookingbackData', 'immunityScoreData']),
		...mapGetters('weatherForecast', ['healthWeatherData']),

		// 日付表示
		computedDate () {
			let duration = ''
			switch (this.selectType) {
				case '1month':
					duration = `${moment.unix(this.toDate).subtract(1, 'months').format('YYYY年MM月DD日')}〜${moment.unix(this.toDate).format('YYYY年MM月DD日')}`
					break
				case '6month':
					duration = `${moment.unix(this.toDate).subtract(6, 'months').format('YYYY年MM月DD日')}〜${moment.unix(this.toDate).format('YYYY年MM月DD日')}`
					break
				case 'year':
					duration = `${moment.unix(this.toDate).subtract(1, 'year').format('YYYY年MM月')}〜${moment.unix(this.toDate).format('YYYY年MM月')}`
					break
			}
			return duration
		},

		// ラインチャートデータセット
		computedLineChartSetData () {
			const lineChartData = {
				value_labels: [],
				value_data: [],
				value_key: Math.round(Math.random() * 1000)
			}

			// リスト整形
			// 日付の設定
			const endUnixTime = this.toDate
			const formatEndDate = moment.unix(endUnixTime).format('YYYY-MM-DD')
			const targetDate = moment(formatEndDate)
			if (this.selectType === '1month') {
				// 1ヶ月間
				const startDate = targetDate.clone().subtract(1, 'months')
				for (let date = startDate; date.isSameOrBefore(targetDate); date.add(1, 'days')) {
					const valueLabel = date.format('YYYY-MM-DD')
					lineChartData.value_labels.push(valueLabel)
					const matchItem = this.scoreTrendRecord.find(item => moment.unix(item.date).format('YYYY-MM-DD') === valueLabel)
					const probabilityItem = matchItem ? Math.round(matchItem.probability * 100) : null
					const distributeItem = probabilityItem === 0 ? null : probabilityItem
					lineChartData.value_data.push(distributeItem)
				}
			} else if (this.selectType === '6month') {
				// 1ヶ月間
				const startDate = targetDate.clone().subtract(6, 'months')
				for (let date = startDate; date.isSameOrBefore(targetDate); date.add(1, 'days')) {
					const valueLabel = date.format('YYYY-MM-DD')
					lineChartData.value_labels.push(valueLabel)
					const matchItem = this.scoreTrendRecord.find(item => moment.unix(item.date).format('YYYY-MM-DD') === valueLabel)
					const probabilityItem = matchItem ? Math.round(matchItem.probability * 100) : null
					const distributeItem = probabilityItem === 0 ? null : probabilityItem
					lineChartData.value_data.push(distributeItem)
				}
			} else {
				// 1年
				const startDate = targetDate.clone().subtract(1, 'year')
				for (let date = startDate; date.isSameOrBefore(targetDate); date.add(1, 'days')) {
					const valueLabel = date.format('YYYY-MM-DD')
					lineChartData.value_labels.push(valueLabel)
					const matchItem = this.scoreTrendRecord.find(item => moment.unix(item.date).format('YYYY-MM-DD') === valueLabel)
					const probabilityItem = matchItem ? Math.round(matchItem.probability * 100) : null
					const distributeItem = probabilityItem === 0 ? null : probabilityItem
					lineChartData.value_data.push(distributeItem)
				}
			}

			return lineChartData
		},

		// ドーナツチャートデータセット
		computedDoughnutChartSetData () {
			const doughnutChartData = {
				probability: 0,
				restProbability: 0,
				key: Math.round(Math.random() * 1000)
			}

			// グラフ数値計算
			const probability = this.probability
			const restProbability = 100 - probability

			doughnutChartData.probability = probability
			doughnutChartData.restProbability = restProbability

			return doughnutChartData
		},

		// 先週アラート出現傾向データセット
		computedPressureAlerts () {
			const data = this.pressure_alerts
			for (let i = 0; i < data.length; i++) {
				data[i].date = moment.unix(data[i].date).format('M/D')
			}
			return data
		},

		// 先々週アラート出現傾向データセット
		computedPrevPressureAlerts () {
			const data = this.prev_pressure_alerts
			for (let i = 0; i < data.length; i++) {
				data[i].date = moment.unix(data[i].date).format('M/D')
			}
			return data
		},

		// 先週の振り返り（ヘルスデータ）
		computedHealthSetData () {
			const healthData = {
				average_step: this.immunityLookingbackData ? this.immunityLookingbackData.average_step : 0, // 平均歩数
				prev_average_step: this.immunityLookingbackData ? this.immunityLookingbackData.prev_average_step : 0, // 先週の平均歩数
				average_sleep: this.immunityLookingbackData ? this.immunityLookingbackData.average_sleep : 0, // 平均睡眠時間
				average_sleep_houre: '', // 平均睡眠時間（時間）
				average_sleep_minutes: '', // 平均睡眠時間（分）
				prev_average_sleep: this.immunityLookingbackData ? this.immunityLookingbackData.prev_average_sleep : 0, // 先週の平均睡眠時間
				prev_average_sleep_houre: '', // 先週の平均睡眠時間（時間）
				prev_average_sleep_minutes: '', // 先週の平均睡眠時間（分）
				average_sunshine: this.immunityLookingbackData ? this.immunityLookingbackData.average_sunshine : 0, // 平均日照時間
				average_sunshine_houre: '', // 平均日照時間（時間）
				average_sunshine_minutes: '', // 平均日照時間（分）
				prev_average_sunshine: this.immunityLookingbackData ? this.immunityLookingbackData.prev_average_sunshine : 0, // 先週の平均日照時間
				prev_average_sunshine_houre: '', // 先週の平均日照時間（時間）
				prev_average_sunshine_minutes: '', // 先週の平均日照時間（分）
				average_temperature_diff: this.immunityLookingbackData ? this.immunityLookingbackData.average_temperature_diff : 0, // 平均気温差
				prev_average_temperature_diff: this.immunityLookingbackData ? this.immunityLookingbackData.prev_average_temperature_diff : 0, // 先週の平均気温差
				pressure_comment: this.immunityLookingbackData ? this.immunityLookingbackData.pressure_comment : '', // 今週の気圧コメント
				sleep_comment: this.immunityLookingbackData ? this.immunityLookingbackData.sleep_comment : '', // 睡眠コメント,
				sleep_and_steps_comment: '' // 先週の歩数と睡眠の前回比較コメント
			}

			// 分を時間に変換
			const durationAverageSleep = moment.duration(healthData.average_sleep, 'minutes')
			healthData.average_sleep_houre = durationAverageSleep.hours()
			healthData.average_sleep_minutes = durationAverageSleep.minutes()

			const durationPrevAverageSleep = moment.duration(healthData.prev_average_sleep, 'minutes')
			healthData.prev_average_sleep_houre = durationPrevAverageSleep.hours()
			healthData.prev_average_sleep_minutes = durationPrevAverageSleep.minutes()

			const durationAverageSunshine = moment.duration(healthData.average_sunshine, 'minutes')
			healthData.average_sunshine_houre = durationAverageSunshine.hours()
			healthData.average_sunshine_minutes = durationAverageSunshine.minutes()

			const durationPrevAverageSunshine = moment.duration(healthData.prev_average_sunshine, 'minutes')
			healthData.prev_average_sunshine_houre = durationPrevAverageSunshine.hours()
			healthData.prev_average_sunshine_minutes = durationPrevAverageSunshine.minutes()

			// 先週の歩数と睡眠の前回比較コメント
			if (healthData.prev_average_step && healthData.prev_average_sleep) {
				const stepUpDownText = healthData.prev_average_step > 0 ? '増えた' : '減った'
				const sleepUpDownText = healthData.prev_average_sleep > 0 ? '増えた' : '減った'
				healthData.sleep_and_steps_comment = `・前回より歩数が${stepUpDownText}ようです。<br>・前回より睡眠時間が${sleepUpDownText}ようです。`
			}

			return healthData
		},

		// 健康天気指数のデータセット
		computedHealthWeatherData () {
			const todayMonth = Number(moment().month()) + 1

			const data = {
				title: '',
				indexName: '',
				level: '',
				comment: '',
				month: todayMonth
			}

			// スケジュール
			// 1月、12月：インフル
			// 2月、3月：花粉
			// 4月、10月、11月：寒暖差
			// 5月、6月：うつ
			// 7月、8月、9月：熱中症
			if (this.healthWeatherData) {
				if (todayMonth === 1 || todayMonth === 12) {
					data.title = 'インフルエンザ'
					data.indexName = 'inful'
					data.level = this.healthWeatherData.records[0].inful_level
					data.comment = this.getHealthComment('inful', this.healthWeatherData.records[0].inful_level)
				} else if (todayMonth === 2 || todayMonth === 3) {
					data.title = '花粉'
					data.indexName = 'pollen'
					data.level = this.healthWeatherData.records[0].pollen_level
					data.comment = this.getHealthComment('pollen', this.healthWeatherData.records[0].pollen_level)
				} else if (todayMonth === 4 || todayMonth === 10 || todayMonth === 11) {
					const tempDifferenceLevel = isNaN(Number(this.healthWeatherData.records[0].temp_difference_level)) ? '1' : this.healthWeatherData.records[0].temp_difference_level
					data.title = '寒暖差'
					data.indexName = 'temp'
					data.level = tempDifferenceLevel
					data.comment = this.getHealthComment('tempDifference', tempDifferenceLevel)
				} else if (todayMonth === 5 || todayMonth === 6) {
					data.title = 'うつ'
					data.indexName = 'utsu'
					data.level = this.healthWeatherData.records[0].utsu_day
					data.comment = this.getHealthComment('utsu', this.healthWeatherData.records[0].utsu_day)
				} else if (todayMonth === 7 || todayMonth === 8 || todayMonth === 9) {
					data.title = '熱中症'
					data.indexName = 'netu'
					data.level = this.healthWeatherData.records[0].netu_move
					data.comment = this.getHealthComment('netu', this.healthWeatherData.records[0].netu_move)
				}
			}

			return data
		},

		// おすすめコラムデータセット
		computedColumnSetData () {
			const todayMonth = Number(moment().month()) + 1

			const columnScheduleList = [ // コラムスケジュール
				[ // 1月
					'post-24769',
					'post-24743'
				],
				[ // 2月
					'post-25267',
					'post-24935'
				],
				[ // 3月
					'post-25746',
					'post-19686'
				],
				[ // 4月
					'post-26257',
					'post-26747'
				],
				[ // 5月
					'post-27120',
					'post-17673'
				],
				[ // 6月
					'post-27383',
					'post-28078'
				],
				[ // 7月
					'post-21690',
					'kanpou01'
				],
				[ // 8月
					'post-22181',
					'post-29036'
				],
				[ // 9月
					'post-22652',
					'post-29384'
				],
				[ // 10月
					'post-23237',
					'post-29784'
				],
				[ // 11月
					'post-23739',
					'post-23864'
				],
				[ // 12月
					'post-24279',
					'post-31271'
				]
			]

			const columnData = {
				url: columnScheduleList[todayMonth - 1],
				month: todayMonth
			}

			return columnData
		}
	},
	methods: {
		// map Vuex actions
		...mapActions('ai', [SET_USER_IMMUNITIES_DATA, SET_IMMUNITIES_LOOKINGBACK_DATA, SET_IMMUNITIES_SCORE_DATA]),
		...mapActions('weatherForecast', [SET_HEALTH_WEATHER]),

		// 免疫力推定結果、免疫力推定振り返り結果取得
		getData () {
			this.isLoading = true

			const today = moment()
			const from = today.set({ 'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0 }).unix()
			const to = today.set({ 'hour': 23, 'minute': 59, 'second': 59, 'millisecond': 999 }).unix()

			const SET_USER_IMMUNITIES_DATA = this.SET_USER_IMMUNITIES_DATA({
				user_id: this.userId,
				user_token: this.userToken
			})
			const SET_IMMUNITIES_LOOKINGBACK_DATA = this.SET_IMMUNITIES_LOOKINGBACK_DATA({
				user_id: this.userId,
				user_token: this.userToken
			})
			const SET_HEALTH_WEATHER = this.SET_HEALTH_WEATHER({
				user_id: this.userId,
				user_token: this.userToken,
				from: from,
				to: to,
				city_code: this.userData.city_code
			})
			Promise.all([SET_HEALTH_WEATHER, SET_USER_IMMUNITIES_DATA, SET_IMMUNITIES_LOOKINGBACK_DATA]).then(res => {
				// 免疫力推定結果データセット
				this.estimationStartDate = moment.unix(this.userImmunitiesData.start_date).format('M/D')
				this.estimationEndDate = moment.unix(this.userImmunitiesData.end_date).format('M/D')
				this.probability = this.userImmunitiesData.probability ? Math.round(this.userImmunitiesData.probability * 100) : 0
				this.prevProbability = this.userImmunitiesData.prev_probability ? this.probability - Math.round(this.userImmunitiesData.prev_probability * 100) : 0

				// 免疫力推定振り返りデータセット
				this.pressure_alerts = this.immunityLookingbackData.pressure_alerts
				this.prev_pressure_alerts = this.immunityLookingbackData.prev_pressure_alerts
				this.prev_pressure_comment = this.immunityLookingbackData.prev_pressure_comment

				this.toDate = this.userImmunitiesData.end_date
				this.isDisabledNextDate = this.userImmunitiesData.end_date

				this.getScoreData()
			}).catch(error => {
				console.log('Error: ', error)
				this.isError = true
				this.isLoading = false
			})
		},

		// 免疫力推定スコア取得
		getScoreData () {
			let fromDate = ''
			const targetDate = moment.unix(this.toDate)
			if (this.selectType === '1month') {
				fromDate = targetDate.clone().subtract(1, 'months').unix()
			} else if (this.selectType === '6month') {
				fromDate = targetDate.clone().subtract(6, 'months').unix()
			} else {
				fromDate = targetDate.clone().subtract(1, 'years').unix()
			}
			this.SET_IMMUNITIES_SCORE_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				from_date: fromDate,
				to_date: this.toDate
			}).then(res => {
				this.setScoreData()
			}).catch(error => {
				// NOTE: エラー処理
				console.log('Error: ', error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		// 免疫力推定スコアデータセット
		setScoreData () {
			this.scoreTrendRecord = this.immunityScoreData.records ? this.immunityScoreData.records : []
		},

		// 日付を変更
		reloadDate (type) {
			switch (this.selectType) {
				case '1month':
					if (type === 'pre') {
						this.toDate = moment.unix(this.toDate).clone().subtract(1, 'months').subtract(1, 'days').unix()
					} else if (type === 'now') {
						this.toDate = moment().unix()
					} else if (type === 'post') {
						this.toDate = moment.unix(this.toDate).clone().add(1, 'months').add(1, 'days').unix()
					}
					this.isDisabledNext = moment.unix(this.isDisabledNextDate).format('YYYY-MM-DD') === moment.unix(this.toDate).format('YYYY-MM-DD')
					break
				case '6month':
					if (type === 'pre') {
						this.toDate = moment.unix(this.toDate).clone().subtract(6, 'months').subtract(1, 'days').unix()
					} else if (type === 'now') {
						this.toDate = moment().unix()
					} else if (type === 'post') {
						this.toDate = moment.unix(this.toDate).clone().add(6, 'months').add(1, 'days').unix()
					}
					this.isDisabledNext = moment.unix(this.isDisabledNextDate).format('YYYY-MM-DD') === moment.unix(this.toDate).format('YYYY-MM-DD')
					break
				case 'year':
					if (type === 'pre') {
						this.toDate = moment.unix(this.toDate).clone().subtract(1, 'year').unix()
					} else if (type === 'now') {
						this.toDate = moment().unix()
					} else if (type === 'post') {
						this.toDate = moment.unix(this.toDate).clone().add(1, 'year').unix()
					}
					this.isDisabledNext = moment.unix(this.isDisabledNextDate).format('YYYY-MM') === moment.unix(this.toDate).format('YYYY-MM')
					break
			}
			this.getScoreData()
		},

		// グラフ表示日数変更
		tabClick (n) {
			if (n.target.innerText === '1ヶ月') {
				this.selectType = '1month'
				this.toDate = this.isDisabledNextDate
			} else if (n.target.innerText === '6ヶ月') {
				this.selectType = '6month'
				this.toDate = this.isDisabledNextDate
			} else {
				this.selectType = 'year'
				this.toDate = this.isDisabledNextDate
			}
			this.reloadDate()
		},

		// 健康天気指数コメント
		getHealthComment (type, level) {
			level = isNaN(level) ? 1 : level
			let commentList = null

			const UTSU_COMMENT_LIST = {
				'5': [
					'天気が崩れる予想です。こんな日は家の中でゴロゴロするのもいいでしょう。無理をせずにお過ごしください。',
					'天気が崩れる予想ですので、家の中でゆっくりするのがおススメです。身体に負担をかけないことが大切です。',
					'天気が崩れる予想です。家の中で映画やドラマを見て過ごすのもいいですよ。感動することでストレスが発散されます。',
					'気圧が大きく下降します。身体への負担が大きくなりますので、無理は禁物です。体調の変化には十分お気を付けください。',
					'気圧が大きく下降します。無理は禁物です。ゆっくりと体を休める時間を作りましょう。'
				],
				'4': [
					'天気が崩れる予想です。薬の飲み忘れなどは体調を崩す原因となりますので、お気を付けください。',
					'天気が崩れる予想です。栄養バランスの良い食事をとり、ゆっくりと体を休めてお過ごしください。',
					'天気が崩れる予想です。気分が落ち込んだ時は、適度に甘いものを食べるとよさそうです。気持ちを落ち着かせる効果があります。',
					'気圧が下降します。ビタミンB3を多く含むレバーやたらこには、神経系の働きがよくなる効果があるのでおススメです。',
					'気圧が下降します。お気を付け下さい。マグネシウムには神経を落ち着かせる効果があります。海藻や大豆、エビなどに多く含まれています。'
				],
				'3': [
					'天気が崩れる予想です。気分の落ち込みに要注意。無理をせずに身体への負担を減らすことが大切です。',
					'日差しがほとんどなくなります。気分の落ち込みに要注意。リラックスしながら音楽を聴くと、自律神経が安定し効果的です。',
					'日差しがほとんどなくなります。ビタミンB12を多く含む鶏レバーやさんまには神経機能を正常に保つ効果がありますのでおススメです。',
					'気圧が下降します。気分の落ち込みに要注意です。趣味をするなど気分転換をして心と体をリフレッシュさせるのもいいですよ。',
					'気圧が下降します。規則正しい生活で体内時計のリズムを保つと、病気の予防効果があります。'
				],
				'2': [
					'気分の落ち込みにご注意を。牡蠣やチーズなどに多く含む亜鉛には、感情を安定させる効果があります。',
					'日差しがやや控えめに。気分の落ち込みにご注意ください。不規則な生活は病気のリスクを高めます。規則正しい生活を心がけましょう。',
					'気圧がやや下降気味。気分の落ち込みにご注意ください。甘いものは気持ちを落ち着かせる効果があります。適度に食べるとよさそうです。',
					'やや注意が必要です。ビタミンB6には免疫力を高める効果があり、ニンニクやマグロ、バナナなどを食べると効果的です。',
					'少し注意が必要な日です。健康のためには栄養バランスの良い食事を3食しっかりととることが大切です。'
				],
				'1': [
					'晴れて気圧も安定するので、比較的安心な1日となりそうです。バランスの良い食生活は、身体の調子を整える助けとなります。',
					'よく晴れるでしょう。太陽の光をシッカリと浴びると脳内でセロトニンが分泌され、気持ちの落ち込みを改善することができます。',
					'よく晴れます。外でウォーキングなどの有酸素運動をしてみてはいかかでしょうか。適度な運動は気分を爽やかにしてくれます。',
					'比較的安心な1日です。早寝早起きをするなど規則正しい生活を心がけましょう。不規則な生活は病気のリスクを高めます。',
					'よく晴れるでしょう。こんな日は気が向いたら、外に出るなどの気分転換もいいかもしれません。'
				]
			}

			const POLLEN_COMMENT_LIST = {
				'5': ['極めて花粉が多くなりそうです。なるべく外出を控えて室内で過ごすのが良いでしょう。'],
				'4': [
					'非常に多いです。症状の重い方は、昼間の外出は出来るだけ控えた方が良いかもしれません。',
					'非常に多いです。防御ｶﾊﾞｰ付きﾒｶﾞﾈは、通常のﾒｶﾞﾈよりも眼に入る花粉の量を更に減らす事ができます。',
					'非常に多いです。花粉は換気扇から入り込むｹｰｽもあるので、ｶﾊﾞｰをして室内へ入るのを防ぎましょう。',
					'非常に多いです。発症していない人も多量に吸い込むと突然発症する可能性もあるので、ﾏｽｸで予防を。',
					'非常に多いです。眼や鼻を洗う際は水道水で洗うと粘膜を痛めやすいので、生理食塩水がｵｽｽﾒです。'
				],
				'3': [
					'多いです。家の中に入る時には、衣類に付着した花粉を手で払い落としてから入るようにしましょう。',
					'多いです。ｺﾝﾀｸﾄﾚﾝｽﾞはｱﾚﾙｷﾞｰ性結膜炎を増幅させる恐れがあるので、ﾒｶﾞﾈに替えた方が良いでしょう。',
					'多いです。ｳｰﾙ製の衣類は花粉が付着しやすいので、綿やﾎﾟﾘｴｽﾃﾙなどの化学繊維が良いでしょう。',
					'多いです。乾燥で鼻や喉の粘膜が傷付くと症状が悪化しますので、加湿器やﾏｽｸで乾燥を防ぎましょう。',
					'多いです。鼻炎薬は眠気の副作用が出る場合もありますので、車の運転は控えましょう。'
				],
				'2': [
					'やや多いです。ﾏｽｸは吸い込む花粉をおよそ1/3～1/6に軽減し、鼻の症状を軽くする効果があります。',
					'やや多いです。ｼﾞｬｹｯﾄや上着の素材は、花粉が付着しにくいﾅｲﾛﾝ製のものがｵｽｽﾒです。',
					'やや多いです。通常のﾒｶﾞﾈでも使用しない時に比べ、眼に入る量を約1/3に減らす事ができます。',
					'やや多いです。洗濯物を外に干したときは、十分に花粉を叩き落してから取り込むようにしましょう。',
					'やや多いです。布団を干すと室内に花粉が侵入しますので、なるべく避けた方が良さそうです。'
				],
				'1': [
					'少ないです。十分な睡眠と規則正しい生活は、免疫機能の改善に役立ち、予防につながります。',
					'少ないです。お酒の飲み過ぎは鼻づまりを悪化させることがありますので、花粉ｼｰｽﾞﾝ中はほどほどに。',
					'少ないです。風邪を引くと鼻や喉の粘膜を傷つけ症状が悪化することがありますので、普段から予防を。',
					'少ないです。喫煙は粘膜を傷つけ、症状を悪化させる恐れがありますので、花粉症の方は控えましょう。',
					'少ないです。日ごろからの適度な運動で免疫力を高め、花粉症の発症や悪化を防ぎましょう。'
				]
			}

			const INFUL_COMMENT_LIST = {
				'5': ['ウイルスの活動が猛烈に活発化し、インフルエンザに非常にかかりやすくなっています。室内や車内ではマスクを付けるなど特に厳重に警戒してください。こまめな手洗いやうがいを忘れずに'],
				'4': ['ウイルスの活動が非常に活発でインフルエンザに非常にかかりやすくなっています。マスクを付けてうがいや手洗いなど対策を十分行ってください。'],
				'3': ['ウイルスの活動が活発になり、インフルエンザにかかりやすくなっています。うがいや手洗いを忘れずに行いましょう。'],
				'2': ['ややウイルスが活動しやすくなっています。うがいや手洗いを心がけるようにして下さい。'],
				'1': ['ウイルスの量が少なくほとんど心配ありません。']
			}

			const NETU_COMMENT_LIST = {
				'5': [
					'危険。高齢者は安静状態でも発生する危険性が大きいですので、涼しいところで過ごすようにしましょう。',
					'危険。なるべく外出しないようにして、空調を利用し室内で過ごしましょう。'
				],
				'4': [
					'厳重警戒。熱中症の危険度が非常に高いので、外出をできるだけ控えたり、風通しを良くして休息を十分にとりましょう。',
					'厳重警戒。汗をかいたときは、水分はもちろん塩分補給もしましょう。',
					'厳重警戒。子供やお年寄りなど体温調節がうまくできない方への気配りを大切にして、積極的な水分補給を促そう！'
				],
				'3': [
					'十分警戒。熱中症の危険度が増すので、十分な予防（十分な休息と水分補給、涼しい服装･帽子）が必要です。',
					'十分警戒。寝不足や食事を抜いた人、体調不調時は特に無理は禁物です。休息と水分補給をしっかり！'
				],
				'2': [
					'十分注意。熱中症の発生の可能性があります。特に暑さに慣れていないときは要注意！',
					'十分注意。屋外労働や作業では、特に注意が必要です。通気性のいい服装と、こまめな休憩・水分補給を！'
				],
				'1': [
					'ほぼ安全。通常は熱中症の危険はほとんどありません。',
					'ほぼ安全。特にマラソンなどの激しい運動でもしないがきりは安全です。',
					'ほぼ安全。晴れた日の車内温度は、このレベルでも直射日光により非常に高くなる恐れあり、お子さんの置き去りはﾀﾞﾒ！'
				]
			}

			const TEMP_DIFFERENCE_COMMENT_LIST = {
				'4': [
					'大幅に気温ジャンプアップ。気温差で体調崩さないようご注意を！！',
					'グングン気温急上昇。体が変化についていけないかも！！'
				],
				'3': [
					'前日よりグンと気温上昇。急な変化に体もビックリ！',
					'前日より大きく気温上昇。服装の調整に気を付けましょう。'
				],
				'2': [
					'前日よりやや気温上昇。室内を適温に保ちましょう。',
					'前日よりも気温アップ。気温に合わせた服装選びを。'
				],
				'1': [
					'大きな気温変化なし。朝晩の気温もチェックしましょう。',
					'前日との気温差小さい。湿度の変化にも気を配りましょう。'
				],
				'-2': [
					'前日よりやや気温低下。服装の調整に気を付けましょう。',
					'前日よりも気温ダウン。体調を整えておきましょう。'
				],
				'-3': [
					'前日よりかなり気温低下。温かい物が美味しく感じられそう。',
					'前日より大きく気温低下。体をほぐしておきましょう。'
				],
				'-4': [
					'前日より大幅に気温急低下。体調管理と服装の調整にご注意を！',
					'ガクッと急激な気温の低下。急な変化で体が疲れてしまいそう。'
				],
				'-5': [
					'前日よりガクーンと気温急降下。自律神経が乱れて体調不良の恐れ。',
					'ジェットコースター並みの気温急降下。体の不調に要注意！！'
				]
			}

			switch (type) {
				case 'utsu':
					// うつ予報の処理
					commentList = UTSU_COMMENT_LIST
					break
				case 'pollen':
					// 花粉レベルの処理
					commentList = POLLEN_COMMENT_LIST
					break
				case 'inful':
					// インフルレベルの処理
					commentList = INFUL_COMMENT_LIST
					break
				case 'netu':
					// 熱中症レベルの処理
					commentList = NETU_COMMENT_LIST
					break
				case 'tempDifference':
					// 寒暖差指数レベルの処理
					commentList = TEMP_DIFFERENCE_COMMENT_LIST
					break
			}
			const rand = Math.floor(Math.random() * commentList[level].length)
			return commentList[level][rand]
		},

		// コラム画面に遷移（URLスキーム）
		onColumn (columnNo) {
			window.location.href = `native://open_column/${columnNo}`
		}
	}
}
</script>

<style lang="scss" scoped>
@import "../../assets/sass/variable";

$spacing-3: 0.1875rem;
$spacing-12: 0.75rem;
$spacing-24: 1.5rem;
$spacing-28: 1.75rem;

$font-size-22: 1.375rem;

$color-salmon: #FF4D4D;
$color-silvergrey: #E6E6E6;
$color-enabled: #999999;
$color-disabled: #CCCCCC;
$color-extended-cora: #F95959;
$color-dark-goldenrod: #9C6F10;
$color-teal: #097D7A;
$color-softyellow: #FFECA5;
$color-lightblue: #B3FAFF;
$color-lightgreen: #D3EDB0;
$color-lightgold: #FFD486;
$color-lightpink: #F7A6AC;
$color-skypurple: #B3BCFF;

.immunity-estimation-result {
	width: 100%;

	&__report {
		padding: 0;
		&--judgement {
			padding: $spacing-20;
			border-bottom: 1px solid $color-silvergrey;
			&-title {
				margin: 0;
				padding-left: $spacing-12;
				border-left: 4px solid $color-salmon;
				font-family: $lp-family-secondary;
				font-size: $font-size-20;
				font-weight: 500;
			}
			&-period {
				margin: $spacing-12 0 $spacing-10;
				text-align: center;
				color: $text-primary;
				font-family: $lp-family-secondary;
				font-size: $font-size-16;
				font-weight: 500;
			}
			&-canvas {
				width: 100%;
				margin: 0;
				padding: 0 26.88%;
				box-sizing: border-box;
			}
			&-complastweek {
				margin: $spacing-16 0 $spacing-26;
				text-align: center;
				color: $text-primary;
				font-family: $lp-family-primary;
				font-size: $font-size-18;
				font-weight: 400;
			}
			&-column {
				width: 100%;
				a {
					text-decoration: none;
					img {
						width: 100%;
						margin: 0;
					}
				}
			}
		}

		&--graph {
			padding: $spacing-20;
			border-bottom: 1px solid $color-silvergrey;
			&-title {
				margin: 0 0 $spacing-24;
				padding-left: $spacing-12;
				border-left: 4px solid $color-salmon;
				font-family: $lp-family-secondary;
				font-size: $font-size-20;
				font-weight: 500;
			}
			&-date {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: $spacing-14 $spacing-10;
				margin: 0;
				background-color: $background-primary;
				font-family: $family-primary;

				.date {
					display: flex;
					align-items: center;
					font-size: $font-size-14;
					font-weight: normal;
					cursor: pointer;

					&.is-disabled {
						pointer-events: none;

						&.is-date::after {
							content: '今日';
							display: block;
							padding: 0 $spacing-6;
							margin: 0 0 0 $spacing-6;
							background-color: $primary;
							font-size: $font-size-9;
							color: $text-seconday;
							border-radius: $spacing-2;
						}
					}
				}

				& > svg path { stroke: $color-enabled; }

				.pre ,
				.post { cursor: pointer; }

				.post.is-disabled {
					cursor: none;
					pointer-events: none;
					path { stroke: $color-disabled; }
				}
			}
			&-canvas {
				width: 100%;
				margin: 0;
				box-sizing: border-box;
			}
			&-tab {
				width: 100%;
				margin: $spacing-16 0 0;
				.list {
					display: flex;
					li {
						width: 33.333%;
						padding: $spacing-8 0;
						text-align: center;
						font-family: $lp-family-primary;
						font-size: $font-size-16;
						font-weight: 400;
						flex: auto;
						list-style-type: none;
						color: $color-extended-cora;
						border: solid 1px $color-extended-cora;
					}
					li:first-child {
						border-radius: 5px 0 0 5px;
						border-right: none;
					}
					li:last-child {
						border-radius: 0 5px 5px 0;
						border-left: none;
					}
					.list-active {
						background: $color-extended-cora;
						color: $text-seconday;
					}
				}
			}
		}

		&--lookback {
			padding: $spacing-20;
			border-bottom: 1px solid $color-silvergrey;
			&-title {
				margin: 0 0 $spacing-18;
				padding-left: $spacing-12;
				border-left: 4px solid $color-salmon;
				font-family: $lp-family-secondary;
				font-size: $font-size-20;
				font-weight: 500;
			}
			&-subtitle {
				position: relative;
				margin: 0 0 $spacing-10;
				padding-left: $spacing-30;
				font-family: $lp-family-secondary;
				font-size: $font-size-16;
				font-weight: 500;
			}
			&-subtitle::before {
				content: '';
				background-image: url(~@/assets/img/immunity-estimation/alert_title_icon.png);
				background-size: contain;
				background-repeat: no-repeat;
				position: absolute;
				left: 0;
				top: 0;
				width: 100%;
				height: 100%;
			}
			&-weekalerts {
				width: 100%;
				.title {
					color: $text-primary;
					font-family: $lp-family-primary;
					font-size: $font-size-14;
					font-weight: 400;
					margin: 0;
					padding: 0 0 $spacing-6 $spacing-4;
					border-bottom: solid 1px $color-disabled;
				}
				.lists {
					display: flex;
					justify-content: space-between;
					list-style: none;
					padding: 0;
					margin: $spacing-6 0 $spacing-8;
					.lastweek {
						width: 12.537%;
						padding: $spacing-3;
						.date {
							margin: 0 0 $spacing-4;
							color: $text-primary;
							text-align: center;
							font-family: $lp-family-primary;
							font-size: $font-size-12;
							font-weight: 400;
						}
						img {
							width: 100%;
						}
					}
					.lastweek:first-child {
						padding-left: 0;
					}
					.lastweek:last-child {
						padding-right: 0;
					}
					.twoweek {
						width: 12.537%;
						padding: $spacing-3;
						.date {
							margin: 0 0 $spacing-4;
							color: $text-primary;
							text-align: center;
							font-family: $lp-family-primary;
							font-size: $font-size-12;
							font-weight: 400;
						}
						.alert {
							margin: 0;
							display: block;
							width: 100%;
							color: $text-bold;
							text-align: center;
							font-family: $lp-family-primary;
							font-size: $font-size-10;
							font-weight: 400;
							padding: $spacing-4 0;
							border-radius: 2px;
						}
						.bg_level_1 {
							background: $color-lightblue;
						}
						.bg_level_2 {
							background: $color-lightgreen;
						}
						.bg_level_3 {
							background: $color-lightgold;
						}
						.bg_level_4 {
							background: $color-lightpink;
						}
						.bg_level_5 {
							background: $color-salmon;
						}
						.bg_level_6 {
							background: $color-skypurple;
						}
					}
					.twoweek:first-child {
						padding-left: 0;
					}
					.twoweek:last-child {
						padding-right: 0;
					}
				}
				.lists:nth-child(2) {
					margin-bottom: 0;
				}
				.comment {
					display: block;
					margin: $spacing-16 0 0;
					background: $background-secondary;
					border-radius: 10px;
					padding: $spacing-16;
					color: $text-primary;
					font-family: $lp-family-primary;
					font-size: $font-size-16;
					font-weight: 400;
				}
			}
			&-physicalcondition {
				width: 100%;
				margin-top: $spacing-24;
				.healthdata {
					display: flex;
					div {
						width: 50%;
						box-sizing: border-box;
						.title {
							margin: 0 0 $spacing-10;
							position: relative;
							padding-left: $spacing-30;
							color: $text-primary;
							font-family: $lp-family-secondary;
							font-size: $font-size-16;
							font-weight: 500;
						}
						.title::before {
							content: '';
							background-size: contain;
							background-repeat: no-repeat;
							position: absolute;
							left: 0;
							top: 0;
							width: 100%;
							height: 100%;
						}
						.step::before {
							background-image: url(~@/assets/img/immunity-estimation/icon_step.png);
						}
						.sleep::before {
							background-image: url(~@/assets/img/immunity-estimation/icon_sleep.png);
						}
						.sunshine::before {
							background-image: url(~@/assets/img/immunity-estimation/icon_sunshine.png);
						}
						.temperature::before {
							background-image: url(~@/assets/img/immunity-estimation/icon_temperature.png);
						}
						.date {
							width: 100%;
							padding-left: $spacing-28;
							p {
								position: relative;
								margin: 0 0 $spacing-2;
								color: $text-primary;
								font-family: $lp-family-primary;
								font-weight: 400;
							}
							.average {
								padding-left: $spacing-16;
								font-size: $font-size-14;
								span {
									font-size: $font-size-24;
								}
							}
							.ratio {
								margin: 0;
								font-size: $font-size-12;
								span {
									font-size: $font-size-14;
								}
							}
							.under::before {
								content: '';
								background-size: contain;
								background-repeat: no-repeat;
								background-image: url(~@/assets/img/immunity-estimation/icon_arrow_under.png);
								position: absolute;
								top: 30%;
								left: 0;
								width: 14px;
								height: 14px;
							}
							.above::before {
								content: '';
								background-size: contain;
								background-repeat: no-repeat;
								background-image: url(~@/assets/img/immunity-estimation/icon_arrow_above.png);
								position: absolute;
								top: 30%;
								left: 0;
								width: 14px;
								height: 14px;
							}
						}
					}
				}
				.healthdata:nth-child(1) {
					margin-bottom: $spacing-16;
				}
				.comment {
					display: block;
					background: $background-secondary;
					border-radius: 10px;
					padding: $spacing-16;
					color: $text-primary;
					font-family: $lp-family-primary;
					font-size: $font-size-16;
					font-weight: 400;
				}
				.advice {
					margin: $spacing-24 0 0;
					padding: $spacing-16;
					background: rgba($color-lightpink, 0.2);
					border-radius: 10px;
					img {
						width: 100%;
						margin: 0 0 $spacing-16;
					}
					p {
						margin: 0 0 $spacing-16;
						color: $text-primary;
						font-family: $lp-family-primary;
						font-size: $font-size-16;
						font-weight: 400;
					}
					.comment2 {
						margin: 0;
					}
				}
			}
		}

		&--hint {
			padding: $spacing-20;
			&-title {
				margin: 0;
				padding-left: $spacing-12;
				border-left: 4px solid $color-salmon;
				font-family: $lp-family-secondary;
				font-size: $font-size-20;
				font-weight: 500;
			}
			&-speechbubble {
				width: 100%;
				margin:  $spacing-24 0;
			}
			&-weatherindex {
				padding: $spacing-24 $spacing-16 0;
				margin: 0 0 $spacing-24;
				border-radius: 10px;
				border: 7px solid $color-dark-goldenrod;
				background: $color-teal;
				.content {
					padding: $spacing-16;
					border-radius: 10px;
					background: $background-primary;
					.title {
						display: block;
						margin: 0 0 $spacing-16;
						padding: $spacing-4;
						border-radius: 20px;
						background: $primary;
						text-align: center;
						color: $text-seconday;
						font-family: $lp-family-secondary;
						font-size: $font-size-18;
						font-weight: 500;
					}
					.index {
						width: 70.848%;
						margin: 0 auto $spacing-16;
						h3 {
							margin: 0 0 $spacing-8;
							text-align: center;
							color: $text-primary;
							font-family: $lp-family-secondary;
							font-size: $font-size-16;
							font-weight: 500;
						}
					}
					.comment {
						border-top: solid 1px $color-enabled;
						padding-top: $spacing-16;
						p {
							margin: 0;
							color: $text-primary;
							font-family: $lp-family-primary;
							font-size: $font-size-16;
							font-weight: 400;
						}
					}
				}
				.weatherlink {
					display: block;
					margin: $spacing-16 0;
					color: $text-seconday;
					text-align: right;
					font-family: $lp-family-primary;
					font-size: $font-size-14;
					font-weight: 400;
					text-decoration: none;
					span {
						font-family: $lp-family-tertiary;
						font-weight: 700;
					}
				}
			}
			&-column {
				.title {
					width: 100%;
					margin: 0 0 $spacing-20;
					font-family: $lp-family-tertiary;
					text-align: center;
					font-weight: 700;
					.first {
						width: 48.4375%;
						display: block;
						position: relative;
						font-size: $font-size-18;
						text-align: center;
						margin: 0 auto $spacing-8;
						color: $primary;
						&::before {
							position: absolute;
							left: -9px;
							top: 0;
							content: "";
							width: 2px;
							height: 100%;
							background: $primary;
							border-radius: 3px;
							transform: rotate(-25deg);
						}
						&::after {
							position: absolute;
							content: "";
							right: -9px;
							top: 0;
							width: 2px;
							height: 100%;
							background: $primary;
							border-radius: 3px;
							transform: rotate(25deg);
						}
					}
					.second {
						width: 90%;
						display: block;
						position: relative;
						margin: 0 auto;
						text-align: center;
						font-size: $font-size-16;
						color: $text-primary;
						z-index: 1;
					}
					.third {
						width: 90%;
						display: block;
						font-size: $font-size-22;
						color: $text-primary;
						position: relative;
						margin: 0 auto;
						z-index: 1;
						&::before {
							content: "";
							display: inline-block;
							position: absolute;
							left: 0;
							bottom: 0;
							width: 100%;
							height: 14px;
							border-radius: 30px;
							background: $color-softyellow;
							z-index: -1;
						}
					}
				}
				.article {
					width: 100%;
					display: flex;
					justify-content: space-between;
					flex-wrap: wrap;
					a {
						width: 47.463%;
						display: block;
						img {
							width: 100%;
						}
					}
				}
			}
		}

		@media (max-width: (360px)) {
			&--graph {
				&-date {
					.date {
						font-size: $font-size-12;
					}
				}
				&-tab {
					.list li {
						font-size: $font-size-14;
					}
				}
			}
			&--lookback {
				&-weekalerts {
					.lists .twoweek .alert {
						font-size: $font-size-8;
					}
					.comment {
						font-size: $font-size-14;
					}
				}
				&-physicalcondition {
					.healthdata {
						div {
							.title {
								font-size: $font-size-14;
							}
							.date {
								.average {
									font-size: $font-size-12;
									span {
										font-size: $font-size-22;
									}
								}
							}
						}
					}
					.comment {
						font-size: $font-size-14;
					}
					.advice p {
						font-size: $font-size-14;
					}
				}
			}
			&--hint {
				&-weatherindex {
					.content {
						.title {
							font-size: $font-size-16;
						}
						.index h3 {
							font-size: $font-size-14;
						}
						.comment p {
							font-size: $font-size-14;
						}
					}
					.weatherlink {
						font-size: $font-size-12;
					}
				}
				&-column {
					.title {
						.first {
							font-size: $font-size-16;
						}
						.second {
							font-size: $font-size-14;
						}
						.third {
							font-size: $font-size-20;
						}
					}
				}
			}
		}
	}
}

// スプライト
.sprite_health_weather_icon {
	margin: 0 auto;
	background-image: url(~@/assets/img/sprite_health_weather_icon.png);
	background-repeat: no-repeat;
	background-size: calc(340px / 2) calc(1064px / 2);
	display: block;

	width: 160px;
	height: 28px;

	.sprite_health_weather_icon_lv {
		@extend .sprite_health_weather_icon;
		margin: 0;

		&[class$="lv0"] {
			width: 0;
		}

		&[class$="lv1"] {
			width: 28px;
		}

		&[class$="lv2"] {
			width: calc(28px * 2 + 5px);
		}

		&[class$="lv3"] {
			width: calc(28px * 3 + 10px);
		}

		&[class$="lv4"] {
			width: calc(28px * 4 + 15px);
		}

		&[class$="lv5"] {
			width: 160px;
		}
	}

	&.kanso_icon {
		background-position: calc(-10px / 2) calc(-238px / 2);

		.kanso_icon_lv {
			background-position: calc(-10px / 2) calc(-162px / 2);
		}
	}

	&.inful_icon {
		background-position: calc(-10px / 2) calc(-86px / 2);

		.inful_icon_lv {
			background-position: calc(-10px / 2) calc(-10px / 2);
		}
	}

	&.utsu_icon {
		background-position: calc(-10px / 2) calc(-694px / 2);

		.utsu_icon_lv {
			background-position: calc(-10px / 2) calc(-618px / 2);
		}
	}

	&.pollen_icon {
		background-position: calc(-10px / 2) calc(-542px / 2);

		.pollen_icon_lv {
			background-position: calc(-10px / 2) calc(-466px / 2);
		}
	}

	&.uv_icon {
		background-position: calc(-10px / 2) calc(-846px / 2);

		.uv_icon_lv {
			background-position: calc(-10px / 2) calc(-770px / 2);
		}
	}

	&.netu_icon {
		background-position: calc(-10px / 2) calc(-390px / 2);

		.netu_icon_lv {
			background-position: calc(-10px / 2) calc(-314px / 2);
		}
	}

	&.temp_icon {
		background-position: calc(-10px / 2) calc(-998px / 2);

		.temp_icon_lv {
			background-position: calc(-10px / 2) calc(-922px / 2);
		}
	}
}
</style>
